/**
 * Splits an artist's blended advance position into one view per funded tranche.
 *
 * The portfolio engine works at artist grain: it pools every UserAdvance row into a single
 * balance and runs one waterfall over it, so there is no per-tranche figure to fetch. What it
 * does return is everything the split needs — the funded amount per month, and the two
 * collection streams that pay the balance down (gross for the company side, artist-net for the
 * artist side). Re-running those streams as a FIFO waterfall recovers each tranche's own
 * balance, recoup date and cashflow.
 *
 * Two properties this deliberately preserves:
 *
 *  - Reconciliation. Every dollar of collection lands on exactly one tranche until the pool is
 *    repaid, so the tranche outstandings sum back to the artist-level outstanding the rest of
 *    the dashboard shows. A tranche view can never contradict the combined view.
 *  - Funding order. A tranche only starts absorbing collections from the month it was funded.
 *    Collections that arrive while every funded tranche is already repaid are banked and
 *    applied to the next tranche when it lands, which keeps the totals identical to a naive
 *    FIFO while making each tranche's balance start on the right month.
 */

export type MonthlyMap = { [month: string]: number }

export type TrancheInput = {
  /** Funded amounts keyed by "YYYYMM". */
  advances: MonthlyMap
  /** Gross collections that pay down the company balance — reported months. */
  royalties: MonthlyMap
  /** Gross collections — projected months. */
  projections: MonthlyMap
  /** Artist-net amounts applied to the advance, reported and projected. */
  artistCollections: { royalties: MonthlyMap; projections: MonthlyMap }
  /** Artist-level recoup target; split across tranches pro rata so the parts sum to the whole. */
  recoupTarget: number
}

export type TrancheSide = {
  /** Collections applied to this tranche, by month. */
  applied: MonthlyMap
  /** Outstanding balance at end of each month. */
  balance: MonthlyMap
  /** Outstanding after the last reported month — projections excluded, as the engine does. */
  outstanding: number
  /** Percentage of the tranche repaid as of the last reported month. */
  recoupPct: number
  /** Month the balance first reaches zero; may fall in the projected tail. */
  recoupMonth: string | null
  /** Months from funding to recoupMonth. */
  recoupLag: number | null
}

export type Tranche = {
  /** 1-based, in funding order. */
  number: number
  fundedMonth: string
  advance: number
  recoupTarget: number
  company: TrancheSide
  artist: TrancheSide
  recouped: boolean
  /** -advance at funding, + collections applied thereafter, indexed from the funding month. */
  normalizedCashflow: number[]
  /** Same cashflow with calendar months attached, for XIRR. */
  datedCashflow: { month: string; amount: number }[]
}

// The engine treats a balance under this many dollars as repaid; mirrored so a tranche does
// not read as outstanding over rounding dust.
const RECOUPED_TOLERANCE = 25

const sortedMonths = (...maps: MonthlyMap[]): string[] => {
  const seen = new Set<string>()
  maps.forEach((map) => Object.keys(map || {}).forEach((month) => seen.add(month)))
  return Array.from(seen).sort()
}

const latestMonth = (map: MonthlyMap): string | null => {
  const keys = Object.keys(map || {})
  return keys.length === 0 ? null : keys.reduce((a, b) => (a > b ? a : b))
}

export const monthDiff = (from: string, to: string): number => {
  const fromYear = parseInt(from.slice(0, 4), 10)
  const fromMonth = parseInt(from.slice(4, 6), 10)
  const toYear = parseInt(to.slice(0, 4), 10)
  const toMonth = parseInt(to.slice(4, 6), 10)
  if ([fromYear, fromMonth, toYear, toMonth].some(isNaN)) return 0
  return (toYear - fromYear) * 12 + (toMonth - fromMonth)
}

type SideAccumulator = {
  remaining: number
  applied: MonthlyMap
  balance: MonthlyMap
  outstanding: number
  recoupMonth: string | null
}

/**
 * Walks one collection stream month by month, paying down tranches in funding order.
 * `bank` carries collections that arrive before the next tranche is funded.
 */
const runWaterfall = (
  fundedMonths: string[],
  amounts: number[],
  reported: MonthlyMap,
  projected: MonthlyMap
): SideAccumulator[] => {
  const state: SideAccumulator[] = amounts.map((amount) => ({
    remaining: amount,
    applied: {},
    balance: {},
    outstanding: amount,
    recoupMonth: null
  }))

  const reportedThrough = latestMonth(reported)
  const months = sortedMonths(reported, projected)
  let bank = 0

  months.forEach((month) => {
    let available = (reported[month] ?? projected[month] ?? 0) + bank
    bank = 0

    state.forEach((tranche, index) => {
      // Not funded yet: this month's collections cannot touch it.
      if (fundedMonths[index] > month) return
      if (tranche.remaining <= 0 || available <= 0) return

      const applied = Math.min(available, tranche.remaining)
      tranche.applied[month] = (tranche.applied[month] ?? 0) + applied
      tranche.remaining -= applied
      available -= applied

      if (tranche.remaining <= RECOUPED_TOLERANCE && tranche.recoupMonth === null) {
        tranche.recoupMonth = month
      }
    })

    // Everything the funded tranches could not absorb waits for the next one. Once every
    // tranche is repaid there is nothing left to hold it against, so it stops accruing.
    const hasUnfundedTranche = fundedMonths.some((funded, index) => funded > month && state[index].remaining > 0)
    bank = hasUnfundedTranche ? available : 0

    state.forEach((tranche, index) => {
      if (fundedMonths[index] > month) return
      tranche.balance[month] = Math.max(tranche.remaining, 0)
    })

    // Outstanding is measured on reported data only — projections estimate the recoup date,
    // they are not collections that have happened.
    if (reportedThrough !== null && month === reportedThrough) {
      state.forEach((tranche) => {
        tranche.outstanding = Math.max(tranche.remaining, 0)
      })
    }
  })

  // No reported months at all: nothing has been collected, so the full amount is outstanding.
  if (reportedThrough === null) {
    state.forEach((tranche, index) => {
      tranche.outstanding = amounts[index]
    })
  }

  return state
}

const toSide = (accumulator: SideAccumulator, advance: number, fundedMonth: string): TrancheSide => ({
  applied: accumulator.applied,
  balance: accumulator.balance,
  outstanding: accumulator.outstanding,
  recoupPct: advance > 0 ? ((advance - accumulator.outstanding) / advance) * 100 : 0,
  recoupMonth: accumulator.recoupMonth,
  recoupLag: accumulator.recoupMonth === null ? null : monthDiff(fundedMonth, accumulator.recoupMonth)
})

export const buildTranches = (input: TrancheInput): Tranche[] => {
  const funded = Object.entries(input.advances || {})
    .filter(([, amount]) => amount > 0)
    .sort(([a], [b]) => (a < b ? -1 : 1))

  if (funded.length === 0) return []

  const fundedMonths = funded.map(([month]) => month)
  const amounts = funded.map(([, amount]) => amount)
  const totalAdvance = amounts.reduce((sum, amount) => sum + amount, 0)

  const companyState = runWaterfall(fundedMonths, amounts, input.royalties || {}, input.projections || {})
  const artistState = runWaterfall(
    fundedMonths,
    amounts,
    input.artistCollections?.royalties || {},
    input.artistCollections?.projections || {}
  )

  return funded.map(([fundedMonth, advance], index) => {
    const company = toSide(companyState[index], advance, fundedMonth)
    const artist = toSide(artistState[index], advance, fundedMonth)

    // Indexed from the tranche's own funding month so the 12M/24M windows mean the same thing
    // they do at artist level: the first N months of this tranche's life.
    const appliedMonths = Object.keys(company.applied).sort()
    const lastMonth = appliedMonths.length ? appliedMonths[appliedMonths.length - 1] : fundedMonth
    const span = Math.max(monthDiff(fundedMonth, lastMonth), 0)

    const normalizedCashflow: number[] = new Array(span + 1).fill(0)
    const datedCashflow: { month: string; amount: number }[] = []
    normalizedCashflow[0] = -advance

    appliedMonths.forEach((month) => {
      const offset = monthDiff(fundedMonth, month)
      if (offset >= 0 && offset < normalizedCashflow.length) normalizedCashflow[offset] += company.applied[month]
    })
    normalizedCashflow.forEach((amount, offset) => {
      if (amount !== 0) datedCashflow.push({ month: addMonths(fundedMonth, offset), amount })
    })

    return {
      number: index + 1,
      fundedMonth,
      advance,
      // Pro rata rather than recomputed from the commission rate: the parts are then guaranteed
      // to add up to the target shown everywhere else.
      recoupTarget: totalAdvance > 0 ? input.recoupTarget * (advance / totalAdvance) : 0,
      company,
      artist,
      recouped: company.outstanding < RECOUPED_TOLERANCE,
      normalizedCashflow,
      datedCashflow
    }
  })
}

export const addMonths = (month: string, offset: number): string => {
  const year = parseInt(month.slice(0, 4), 10)
  const monthIndex = parseInt(month.slice(4, 6), 10)
  if (isNaN(year) || isNaN(monthIndex)) return month
  const total = year * 12 + (monthIndex - 1) + offset
  return `${String(Math.floor(total / 12)).padStart(4, "0")}${String((total % 12) + 1).padStart(2, "0")}`
}

// ---- return metrics ---------------------------------------------------------

// numpy_financial.irr solves for the root of the NPV polynomial; for an advance cashflow
// (one outflow, then inflows) that root is unique, so bisection finds the same answer without
// pulling a solver dependency into the bundle.
const npvAtRate = (rate: number, cashflow: number[]): number =>
  cashflow.reduce((sum, amount, index) => sum + amount / Math.pow(1 + rate, index), 0)

const bisect = (valueAt: (rate: number) => number, low: number, high: number): number | null => {
  let lowValue = valueAt(low)
  let highValue = valueAt(high)
  if (!isFinite(lowValue) || !isFinite(highValue) || lowValue * highValue > 0) return null

  for (let i = 0; i < 200; i++) {
    const mid = (low + high) / 2
    const midValue = valueAt(mid)
    if (!isFinite(midValue)) return null
    if (Math.abs(midValue) < 1e-9 || high - low < 1e-12) return mid
    if (lowValue * midValue <= 0) {
      high = mid
      highValue = midValue
    } else {
      low = mid
      lowValue = midValue
    }
  }
  return (low + high) / 2
}

/** Monthly IRR of a period-indexed cashflow, annualized the same way the backend does. */
export const annualizedIrr = (cashflow: number[]): number | null => {
  if (cashflow.length < 2) return null
  const hasInflow = cashflow.some((amount) => amount > 0)
  const hasOutflow = cashflow.some((amount) => amount < 0)
  if (!hasInflow || !hasOutflow) return null

  const monthly = bisect((rate) => npvAtRate(rate, cashflow), -0.999999, 10)
  if (monthly === null) return null

  const annual = Math.pow(1 + monthly, 12) - 1
  return isFinite(annual) ? annual : null
}

/** IRR over the tranche's first `months` periods — the 12M / 24M / 36M windows. */
export const windowedIrr = (cashflow: number[], months: number): number | null =>
  annualizedIrr(cashflow.length >= months ? cashflow.slice(0, months) : cashflow)

const MONTH_DAYS = 30.4375

/** Date-weighted return over the whole tranche, matching pyxirr's actual/365 convention. */
export const xirr = (cashflow: { month: string; amount: number }[]): number | null => {
  if (cashflow.length < 2) return null
  const hasInflow = cashflow.some((entry) => entry.amount > 0)
  const hasOutflow = cashflow.some((entry) => entry.amount < 0)
  if (!hasInflow || !hasOutflow) return null

  const start = cashflow[0].month
  const flows = cashflow.map((entry) => ({
    // Month keys carry no day component, so months are converted with the average month
    // length — the same first-of-month spacing the backend feeds pyxirr.
    years: (monthDiff(start, entry.month) * MONTH_DAYS) / 365,
    amount: entry.amount
  }))

  const rate = bisect(
    (r) => flows.reduce((sum, flow) => sum + flow.amount / Math.pow(1 + r, flow.years), 0),
    -0.999999,
    1000
  )
  return rate !== null && isFinite(rate) ? rate : null
}

export const trancheEconomicMoic = (tranche: Tranche): number => {
  const collected = Object.values(tranche.company.applied).reduce((sum, amount) => sum + amount, 0)
  return tranche.advance > 0 ? collected / tranche.advance : 0
}
